// noinspection ES6UnusedImports
import { saveAs } from 'file-saver'
import { SelectOption } from '@/components/AntdForm/antd-components.def'
import { flatMap, isArray, isPlainObject, keys, map } from 'lodash-es'
import dayjs from 'dayjs'

export function toDictionary<T, K extends string | number | symbol, V>(data: T[], keySelector: (args: T) => K, valueSelector: (args: T) => V) {
  const dict = {} as Record<K, V>
  data.forEach(x => {
    dict[keySelector(x)] = valueSelector(x)
  })

  return dict
}

export const dictToSelectOptions = <T extends Record<string, string>, >(enumObj: T): SelectOption[] => {
  return Object.entries(enumObj).map(([key, val]) => {
    return {
      label: key,
      value: val.toString(),
    } as SelectOption
  })
}

export const getFormattedTimestamp = () => {
  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = String(currentDate.getMonth() + 1).padStart(2, '0')
  const day = String(currentDate.getDate()).padStart(2, '0')
  const hours = String(currentDate.getHours()).padStart(2, '0')
  const minutes = String(currentDate.getMinutes()).padStart(2, '0')
  const seconds = String(currentDate.getSeconds()).padStart(2, '0')
  const milliseconds = String(currentDate.getMilliseconds()).padStart(3, '0')

  return `${year}-${month}-${day}T${hours}${minutes}${seconds}.${milliseconds}`
}

export const enumMapToSelectOptions = (object: Record<string, string>): SelectOption[] => {
  return Object.entries(object).map(([key, val]) => {
    return {
      label: val,
      value: key,
    } as SelectOption
  })
}

export const stringArrayToSelectOptions = (arr: string[]): SelectOption[] => {
  return arr.map(x => {
    return {
      label: x,
      value: x,
    } as SelectOption
  })
}

export const downloadFile = (filename: string, blob: Blob) => {
  saveAs(blob, `${filename}`)
}

export const downloadDocx = (filename: string, blob: Blob) => {
  saveAs(blob, `${filename}.docx`)
}

export const downloadExcel = (filename: string, blob: Blob) => {
  saveAs(blob, `${filename}.xlsx`)
}

export const downloadJsonFile = (data: string, filename: string) => {
  const fileBlob = new Blob([data], { type: 'application/json' })
  const url = URL.createObjectURL(fileBlob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export const termSheetFileName = (employeeName: string, termSheetType: string) => {
  const timestamp = getFormattedTimestamp()
  return `${termSheetType} Term Sheet - ${employeeName}_${timestamp}.json`
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const objectLeavePaths = (obj: any, parentKey?: string): string[] => {
  let result: string[]
  if (isArray(obj)) {
    let idx = 0
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result = flatMap(obj, function (obj: any) {
      return objectLeavePaths(obj, `${parentKey || ''}[${idx++}]`)
    })
  } else if (isPlainObject(obj)) {
    result = flatMap(keys(obj), function (key) {
      return map(objectLeavePaths(obj[key], key), function (subKey) {
        return (parentKey ? `${parentKey}.` : '') + subKey
      })
    })
  } else {
    result = parentKey ? [parentKey] : []
  }

  return result
}
export const sleep = (delayMs: number) => new Promise((resolve) => setTimeout(resolve, delayMs))

export const toDisplayNum = (value?: number | null, maximumFractionDigits = 2) => {
  if (value == undefined) return undefined

  return new Intl.NumberFormat('default', {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value)
}

export const toDisplayNumSf = (value?: number | null, maximumSignificantDigits = 5) => {
  if (value == undefined) return undefined

  return new Intl.NumberFormat('default', {
    minimumSignificantDigits: 1,
    maximumSignificantDigits,
  }).format(value)
}

export const toPercent = (value: number, maximumFractionDigits = 2): string => {
  return new Intl.NumberFormat('default', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value)
}

export const toDateTime = (value: string): string => {
  if (!value) return ''

  return dayjs(value).format('YYYY-MM-DD HH:mm:ss')
}

export const toBooleanText = (value?: boolean) => value ? 'Yes' : 'No'

export const stringToRgb = (str: string) => {
  str = str.toLowerCase()
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  let color = '#'
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF
    color += (`00${value.toString(16)}`).slice(-2)
  }
  return color
}

export const getArrayChanges = <T>(originalData: T[], newData: T[], selector: (item: T) => string | number): {
  add: T[],
  remove: T[]
} => {
  return {
    add: newData.filter(newItem => !originalData.some(originalItem => selector(originalItem) === selector(newItem))),
    remove: originalData.filter(originalItem => !newData.some(newItem => selector(newItem) === selector(originalItem))),
  }
}